import {
  GreyGrey10,
  Link,
  Primary13Regular,
  RedRed60,
  Primary13SemiBold,
  GreyGrey70,
  Box,
  Flex,
  Grid
} from '@sequencehq/core-components'
import {
  Checkbox,
  InputSelectControlField,
  PercentageField,
  PriceField,
  RadioButtonGroup,
  TextInputField,
  TextOverflow
} from '@sequencehq/core-components'
import { useDiscountEditorForm } from 'modules/Cube/view/common/drawers/discountEditor/drawer/content/DiscountEditorForm/useDiscountEditorForm'
import {
  ApplyDiscountTo,
  DiscountCalculationType,
  SeatDiscountType
} from 'modules/Cube/view/common/drawers/discountEditor/drawer/discountEditor.types'

const FIELD_STYLES = {
  label: {
    ...Primary13SemiBold,
    color: GreyGrey70
  }
}

export const DiscountEditorForm = ({ padding }: { padding?: string }) => {
  const { fieldsConfig } = useDiscountEditorForm()

  return (
    <Flex
      padding={padding}
      gap="16px"
      flexDirection="column"
      data-testid="discountEditor.form"
    >
      <TextInputField
        label="Name"
        data-testid="discount.name"
        value={fieldsConfig.name.value}
        onChange={e => fieldsConfig.name.onChange(e.target.value)}
        isDisabled={fieldsConfig.name.disabled}
        validationErrors={fieldsConfig.name.validationErrors}
        description={
          <>
            The discount name will be displayed on the invoice.{' '}
            <Link
              to="https://docs.sequencehq.com/billing/create-billing-schedules#recurring-discounts"
              isExternal
              color="indigo.50"
            >
              Learn more
            </Link>
          </>
        }
        styles={FIELD_STYLES}
      />
      <Box>
        <Box {...FIELD_STYLES.label} marginBottom="8px">
          Type
        </Box>
        <RadioButtonGroup
          options={fieldsConfig.discountCalculationType.options}
          value={fieldsConfig.discountCalculationType.value}
          isDisabled={fieldsConfig.discountCalculationType.disabled}
          fullWidth
          onChange={newValue =>
            fieldsConfig.discountCalculationType.onChange(
              newValue as DiscountCalculationType
            )
          }
          data-testid="discount.discountCalculationType"
        />
      </Box>
      {!fieldsConfig.nominalValue.hidden && (
        <PriceField
          label="Amount"
          value={fieldsConfig.nominalValue.value}
          onChange={fieldsConfig.nominalValue.onChange}
          validationErrors={fieldsConfig.nominalValue.validationErrors}
          isDisabled={fieldsConfig.nominalValue.disabled}
          currency={fieldsConfig.nominalValue.currency}
          data-testid="discount.nominalValue"
          styles={FIELD_STYLES}
          variant="v2"
        />
      )}
      {!fieldsConfig.percentageValue.hidden && (
        <PercentageField
          label="Amount"
          value={fieldsConfig.percentageValue.value}
          onChange={fieldsConfig.percentageValue.onChange}
          validationErrors={fieldsConfig.percentageValue.validationErrors}
          isDisabled={fieldsConfig.percentageValue.disabled}
          data-testid="discount.percentageValue"
          styles={FIELD_STYLES}
          variant="v2"
        />
      )}

      <Box>
        {/** The applyTo field - at some point we may want to extract this input style and create an input from it! */}
        <Box {...FIELD_STYLES.label} marginBottom="8px">
          Apply to
        </Box>
        <Box
          marginBottom="16px"
          borderRadius="6px"
          border={
            fieldsConfig.applyTo.validationErrors.length
              ? `1px solid ${RedRed60}`
              : undefined
          }
          overflow={'hidden'}
        >
          <Box width="100%">
            <RadioButtonGroup
              options={fieldsConfig.applyTo.options}
              value={fieldsConfig.applyTo.value}
              isDisabled={fieldsConfig.applyTo.disabled}
              fullWidth
              onChange={newValue =>
                fieldsConfig.applyTo.onChange(newValue as ApplyDiscountTo)
              }
              data-testid="discount.applyTo"
            />
          </Box>
          {fieldsConfig.applyTo.value === 'SPECIFIC' && (
            <Box>
              {fieldsConfig.applyTo.prices.map(price => (
                <Grid
                  data-testid={`discount.applyTo.price.${price.id}`}
                  key={price.id}
                  alignItems="center"
                  padding="8px"
                  templateColumns="auto 3fr 2fr"
                  cursor={price.disabled ? 'default' : 'pointer'}
                  {...Primary13Regular}
                  _hover={{
                    backgroundColor: GreyGrey10
                  }}
                  {...(price.disabled
                    ? {
                        opacity: 0.4
                      }
                    : {})}
                  onClick={() => {
                    !price.disabled && price.toggleSelection()
                  }}
                >
                  <Box paddingRight="8px">
                    <Checkbox
                      data-testid={`discount.applyTo.price.${price.id}.checkbox`}
                      isChecked={price.selected}
                      disabled={price.disabled}
                    />
                  </Box>
                  <Box paddingRight="8px" overflow={'hidden'}>
                    <TextOverflow {...Primary13Regular} splitPercentage={100}>
                      {price.label}
                    </TextOverflow>
                  </Box>
                  <Box overflow={'hidden'}>
                    <TextOverflow
                      {...Primary13Regular}
                      data-testid={`discount.applyTo.price.${price.id}`}
                      splitPercentage={100}
                      textAlign="right"
                    >
                      {price.price}
                    </TextOverflow>
                  </Box>
                </Grid>
              ))}
            </Box>
          )}
        </Box>
        {Boolean(fieldsConfig.applyTo.validationErrors.length) && (
          <Box
            marginTop="-8px"
            marginBottom="16px"
            key={fieldsConfig.applyTo.validationErrors[0].message}
            {...Primary13Regular}
            color={RedRed60}
          >
            {fieldsConfig.applyTo.validationErrors[0].message}
          </Box>
        )}
      </Box>
      {!fieldsConfig.seatDiscountType.hidden && (
        <InputSelectControlField
          data-testid="discount.seat-discount-type"
          label="Discount scope"
          description="Choose which seat-based charges to discount"
          placeholder="Select scope"
          value={fieldsConfig.seatDiscountType.value}
          options={fieldsConfig.seatDiscountType.options}
          onChange={newValue =>
            fieldsConfig.seatDiscountType.onChange(newValue as SeatDiscountType)
          }
          validationErrors={fieldsConfig.seatDiscountType.validationErrors}
          disabled={fieldsConfig.seatDiscountType.disabled}
          styles={FIELD_STYLES}
        />
      )}
    </Flex>
  )
}
